import { Box, Link as MuiLink, Paper, Typography } from "@mui/material";
import { Link } from "react-router";
import { useTheme } from "../../context/ThemeContext";
import { PartyValueInCase } from "../../shared/ValueStatistics";
import {
  Sx,
  TABULAR_NUMERALS,
  Tone,
  toneColors,
} from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { card } from "./PortfolioSection";

/** An amount written in full needs this much room, in px. */
const FIGURE_MIN_WIDTH = 180;

/** The room between two figures side by side, in px. */
const FIGURE_GAP = 26;

/** A figure of the portfolio, as a card states it. */
export interface Figure {
  label: string;
  value: string;
  /** What a total or an average is taken over. */
  note?: string;
  /** The good or the bad end of a range; a total or an average has no tone. */
  tone?: Exclude<Tone, "neutral">;
  /** Whose figure an end of a range is, as a link into that party's case. */
  who?: { text: string; to: string };
}

/**
 * The highest or the lowest of a statistic as a figure: in the tone of the
 * end it is, and naming the party and the case it belongs to. A statistic
 * over nothing has no such end, and so no figure.
 */
export const extremeFigure = (
  extreme: PartyValueInCase | undefined,
  {
    label,
    tone,
    format,
    who,
    page,
  }: {
    label: string;
    tone: NonNullable<Figure["tone"]>;
    format: (value: number) => string;
    who: (extreme: PartyValueInCase) => string;
    /** The page of the case that the figure is read on. */
    page: string;
  },
): Figure | undefined =>
  extreme && {
    label,
    tone,
    value: format(extreme.value),
    who: { text: who(extreme), to: `/cases/${extreme.caseId}/${page}` },
  };

/** Some figures of the portfolio as a card: side by side, or one under another. */
export const FigureCard = ({
  title,
  figures,
  stacked = false,
  sx,
}: {
  title: string;
  /** A figure there is none of is left out. */
  figures: (Figure | undefined)[];
  stacked?: boolean;
  sx?: Sx;
}) => {
  const { theme } = useTheme();
  const tones = toneColors(theme);

  return (
    <Paper
      component="section"
      variant="outlined"
      sx={[card, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <SectionLabel component="h3">{title}</SectionLabel>
      <Box
        sx={{
          display: "grid",
          // two to a row, and one where two amounts would not fit in full
          gridTemplateColumns: stacked
            ? "minmax(0, 1fr)"
            : `repeat(auto-fit, minmax(max(${FIGURE_MIN_WIDTH}px, calc(50% - ${FIGURE_GAP}px)), 1fr))`,
          columnGap: `${FIGURE_GAP}px`,
          rowGap: 2,
          mt: 1.75,
        }}
      >
        {figures.map(
          (figure) =>
            figure && (
              <div key={figure.label}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  {figure.label}
                </Typography>
                <Typography
                  variant="h5"
                  component="p"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-.015em",
                    lineHeight: 1.25,
                    color: figure.tone && tones[figure.tone],
                    ...TABULAR_NUMERALS,
                  }}
                >
                  {figure.value}
                </Typography>
                {figure.note && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary" }}
                  >
                    {figure.note}
                  </Typography>
                )}
                {figure.who && (
                  <MuiLink
                    component={Link}
                    to={figure.who.to}
                    underline="hover"
                    sx={{
                      display: "block",
                      typography: "body2",
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {figure.who.text}
                  </MuiLink>
                )}
              </div>
            ),
        )}
      </Box>
    </Paper>
  );
};
